import { Suspense, lazy } from 'react';
import type { ComponentType } from 'react';
import { PageLoader } from '@/components/ui/BrandMark';

/**
 * Route-level code splitting with a stale-chunk guard.
 *
 * Each page loaded through lazyPage() becomes its own build chunk, fetched
 * the first time the user visits that route. The guard handles the deploy
 * race: chunk filenames are content-hashed, so a user who loaded the app
 * before a deploy can click into a not-yet-visited page and request a chunk
 * file that no longer exists. When that first dynamic import fails we reload
 * the page once (picking up the new index.html and fresh chunk names); a
 * sessionStorage flag stops a broken network from turning that into a reload
 * loop — the second failure falls through to the router's errorElement.
 */

const RELOAD_KEY = 'lazy-chunk-reloaded';

// sessionStorage can throw (some privacy modes) — degrade to "no guard".
function flagGet(): boolean {
  try { return sessionStorage.getItem(RELOAD_KEY) === '1'; } catch { return true; }
}
function flagSet(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(RELOAD_KEY, '1');
    else sessionStorage.removeItem(RELOAD_KEY);
  } catch { /* ignore */ }
}

export function lazyPage(load: () => Promise<{ default: ComponentType }>) {
  const Page = lazy(() =>
    load().then(
      (mod) => {
        flagSet(false); // healthy load — re-arm the guard for the next deploy
        return mod;
      },
      (err) => {
        if (!flagGet()) {
          flagSet(true);
          window.location.reload();
          // Never resolves — the page is reloading; stops React from
          // momentarily rendering the error boundary underneath.
          return new Promise<{ default: ComponentType }>(() => {});
        }
        throw err;
      },
    ),
  );
  return (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  );
}
